import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectBaseProps, SelectOption } from 'govwell-ui/components/Select/types'
import { FeeScheduleFragment } from 'types/graphql'

const getOptionFromFeeSchedule = (fs: FeeScheduleFragment): SelectOption<number> => {
  return {
    label: fs.name,
    value: fs.id,
  }
}

type Props = {
  caption?: React.ReactNode
  errorMessage?: string
  getIsOptionDisabled?: (option: SelectOption<number>) => boolean
  isInvalid?: boolean
  isClearable?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  width?: React.CSSProperties['width']
  isDisabled?: boolean
  placeholder?: string
  feeSchedules?: FeeScheduleFragment[]
  onValueChange: (value: number | null) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  value?: number | null
}

const FeeScheduleSelect = ({
  caption,
  errorMessage,
  isInvalid,
  isClearable,
  isRequired,
  label,
  getIsOptionDisabled,
  onValueChange,
  onBlur,
  value,
  width,
  isDisabled,
  placeholder = 'Select a fee schedule',
  feeSchedules = [],
}: Props) => {
  const options: SelectOption<number>[] = useMemo(
    () => feeSchedules.map(getOptionFromFeeSchedule),
    [feeSchedules]
  )

  const selectedOption = useMemo(() => {
    return options.find((o) => o.value === value)
  }, [options, value])

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<number> | undefined) => {
      onValueChange(option?.value ?? null)
    },
    [onValueChange]
  )

  const getOptionKey = useCallback((option: SelectOption<number>) => {
    return option.value
  }, [])

  const selectBaseProps: SelectBaseProps<number> = {
    caption,
    errorMessage,
    getOptionKey,
    isClearable,
    isRequired,
    label,
    options,
    placeholder,
    width,
    isDisabled,
    getIsOptionDisabled,
  }

  return (
    <Select
      {...selectBaseProps}
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOption}
      onBlur={onBlur}
      isRequired={isRequired}
      errorMessage={errorMessage}
      isInvalid={isInvalid}
      caption={caption}
    />
  )
}

export default React.memo(FeeScheduleSelect)
